/* eslint-disable @typescript-eslint/no-explicit-any */
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { Button, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { IOptionFM } from '../types';
import AddDocumentForm from './AddDocumentForm';
import { supplierService } from 'features/inventory/suppliers/api/SupplierService';
import { fmTagService } from '../api/FMTagService';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { EnhancedDialog } from 'components/enhanced';

interface IAddDocumentDialog {
  onSubmit: (values, suppliers) => void;
  currentFolder?: string;
  originalService?: string;
  objectId?: number;
  isFornitore?: boolean;
}

export const AddDocumentDialog = ({
  onSubmit,
  currentFolder,
  objectId,
  originalService,
  isFornitore
}: IAddDocumentDialog) => {
  const { t } = useTranslation();

  const [categories, setCategories] = useState<IOptionFM[]>([]);
  const [suppliers, setSuppliers] = useState<Option[]>([]);
  const [warehouses, setWarehouses] = useState<Option[]>([]);
  const [openDocumentDialog, setOpenDocumentDialog] = useState(false);

  // DIALOG functions management
  const handleOpenDocumentDialog = () => setOpenDocumentDialog(true);
  const handleCloseDocumentDialog = () => setOpenDocumentDialog(false);

  // TAGs management
  const startingUploadTags = () => {
    fmTagService
      .getAllPaginated(0, 0)
      .then((response: any) => {
        setCategories(response.results || []);
      })
      .catch((err: any) => console.error('Failed to load tags dictionary:', err));

    warehouseService
      .getAllBaseInformation()
      .then(setWarehouses)
      .catch((err: any) => console.error('Failed to load warehouses list:', err));

    supplierService
      .getAllBaseInformation()
      .then((res: any[]) => {
        setSuppliers(
          (res || []).map((value) => ({
            id: value.id,
            code: `${value.code} - ${value.company_name}`,
            label: value.company_name
          }))
        );
      })
      .catch((err: any) => console.error('Failed to load suppliers reference metadata:', err));
  };

  /**
   * Function to handle the submission of a new document.
   * Uploads the document to the API and refreshes the list of documents.
   *
   * @param {Object} values - The form values with the document details.
   */
  const onSubmitNewDocument = async (values) => {
    if (values.attachment == null || values.attachment == undefined) {
      toast.error(t('entities.document.empty-error'));
      return;
    }

    onSubmit(values, suppliers);

    // close document dialog
    handleCloseDocumentDialog();
  };

  useEffect(() => {
    startingUploadTags();
  }, []);

  const isUploadDisabled = currentFolder && currentFolder !== '/';
  return (
    <>
      <Tooltip title={t('entities.document.file-upload')} arrow placement="top">
        <span>
          <Button
            variant="contained"
            color="primary"
            disabled={isUploadDisabled}
            onClick={handleOpenDocumentDialog}
            sx={{
              height: 40,
              width: 40,
              minWidth: 40,
              p: 0,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}>
            <NoteAddIcon fontSize="small" />
          </Button>
        </span>
      </Tooltip>
      <EnhancedDialog
        open={openDocumentDialog}
        onClose={handleCloseDocumentDialog}
        title={t('entities.document.title')}
        submitLabel={t('actions.save', 'Salva')}
        cancelLabel={t('actions.reset', 'Annulla')}
        maxWidth="md"
        submitButtonProps={{
          type: 'submit',
          form: 'addNewDocument'
        }}>
        <AddDocumentForm
          onSubmit={onSubmitNewDocument}
          categorys={categories}
          suppliers={isFornitore ? null : suppliers}
          warehouses={warehouses}
        />
      </EnhancedDialog>
    </>
  );
};
